<template>
  <div class="agenda-form">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            {{ isUpdate ? "Editar Agendamento" : "Novo Agendamento" }}
          </span>
        </h3>
      </div>

      <div class="card-body pt-0">
        <div class="agenda-steps mb-8">
          <button
            v-for="item in steps"
            :key="item.id"
            type="button"
            class="agenda-step"
            :class="{ active: step === item.id, done: step > item.id }"
            @click="goToStep(item.id)"
          >
            <span>{{ item.id }}</span>
            {{ item.label }}
          </button>
        </div>

        <div v-if="step === 1">
          <b-row>
            <b-col md="6">
              <b-form-group label="Cliente" label-for="cliente">
                <VueSelect
                  ref="buscarCliente"
                  label="nome"
                  :filterable="false"
                  :options="clientes"
                  v-model="form.cliente"
                  @input="setCliente"
                  @search="onSearchCliente"
                  :clearSearchOnSelect="true"
                >
                  <template slot="no-options"
                    >Informe o nome do cliente</template
                  >
                  <template slot="option" slot-scope="option">
                    <div class="d-flex justify-content-between">
                      <span>{{ option.codigo }} - {{ option.nome }}</span>
                      <small class="text-muted">{{
                        formatCpfCnpj(option.cnpj_cpf)
                      }}</small>
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    {{ option.nome }}
                  </template>
                </VueSelect>
                <button
                  type="button"
                  class="btn btn-light-primary btn-sm font-weight-bold mt-3"
                  @click="openNovoClienteModal"
                >
                  Novo Cliente
                </button>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Responsável da agenda">
                <VueSelect
                  v-if="currentUser.admin"
                  ref="buscarUsuario"
                  label="nome"
                  :filterable="false"
                  :options="usuarios"
                  v-model="form.usuario"
                  @input="setUsuario"
                  @search="onSearchUsuario"
                  :clearSearchOnSelect="true"
                >
                  <template slot="no-options">
                    Informe o nome do usuário
                  </template>
                  <template slot="option" slot-scope="option">
                    {{ usuarioLabel(option) }}
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    {{ usuarioLabel(option) }}
                  </template>
                </VueSelect>
                <b-form-input v-else :value="responsavelLabel" disabled />
              </b-form-group>
            </b-col>
          </b-row>

          <div v-if="form.cliente" class="cliente-resumo mb-6">
            <div>
              <strong>{{ form.cliente.nome }}</strong>
              <span class="text-muted d-block" v-if="form.cliente.cnpj_cpf">
                {{ formatCpfCnpj(form.cliente.cnpj_cpf) }}
              </span>
            </div>
            <div v-if="clienteTelefone" class="text-muted">
              Telefone: {{ clienteTelefone }}
            </div>
            <div v-if="clienteEmail" class="text-muted">
              E-mail: {{ clienteEmail }}
            </div>
          </div>

          <b-row>
            <b-col md="8">
              <b-form-group label="Título interno" label-for="titulo">
                <b-form-input
                  id="titulo"
                  v-model="form.titulo"
                  placeholder="Opcional"
                />
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group label="Duração" label-for="duracao">
                <b-form-input
                  id="duracao"
                  type="number"
                  min="1"
                  max="1440"
                  step="1"
                  v-model.number="form.duracao_minutos"
                  placeholder="Minutos"
                  @input="changeDuration"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-form-group label="Observações" label-for="observacoes">
                <b-form-textarea
                  id="observacoes"
                  rows="2"
                  v-model="form.observacoes"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div v-if="step === 2">
          <b-row>
            <b-col md="8">
              <div class="agenda-calendar-header">
                <button
                  type="button"
                  class="btn btn-light btn-icon"
                  @click="moveDate(-1)"
                >
                  <ChevronLeft :size="20" />
                </button>
                <b-form-input
                  type="date"
                  v-model="form.data"
                  @change="changeDate"
                  class="agenda-date-input"
                />
                <button
                  type="button"
                  class="btn btn-light btn-icon"
                  @click="moveDate(1)"
                >
                  <ChevronRight :size="20" />
                </button>
                <button
                  type="button"
                  class="btn btn-light-primary font-weight-bold"
                  @click="setToday"
                >
                  Hoje
                </button>
                <button
                  type="button"
                  class="btn btn-light-primary font-weight-bold"
                  :disabled="searchingNextAvailable"
                  @click="goToNextAvailableDate"
                >
                  {{
                    searchingNextAvailable
                      ? "Buscando..."
                      : "Próximo disponível"
                  }}
                </button>
              </div>

              <div class="agenda-day-title">
                <h4 class="mb-1">{{ formattedSelectedDate }}</h4>
                <div class="agenda-day-meta">
                  <span>Expediente: {{ expedienteLabel }}</span>
                  <span>{{ form.duracao_minutos }} min por agendamento</span>
                  <span>{{ availableSlotsCount }} horários livres</span>
                </div>
              </div>

              <div v-if="loadingHorarios" class="agenda-loading">
                <b-spinner small class="mr-2" />
                Carregando horários...
              </div>
              <div v-else-if="horariosError" class="alert alert-light-danger">
                <div class="d-flex align-items-center justify-content-between">
                  <span>{{ horariosError }}</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-light-danger font-weight-bold"
                    @click="loadHorarios"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
              <div
                v-else-if="horarios.expediente && horarios.expediente.fechado"
                class="alert alert-light-warning"
              >
                <div class="d-flex align-items-center justify-content-between">
                  <span
                    >Domingo não possui expediente padrão para
                    agendamentos.</span
                  >
                  <button
                    type="button"
                    class="btn btn-sm btn-light-warning font-weight-bold"
                    @click="moveDate(1)"
                  >
                    Próximo dia
                  </button>
                </div>
              </div>
              <div v-else-if="!hasSlots" class="agenda-empty">
                <h5 class="font-weight-bolder mb-2">
                  Nenhum horário foi recebido.
                </h5>
                <p class="text-muted mb-4">
                  Tente recarregar a disponibilidade ou escolha outro dia.
                </p>
                <button
                  type="button"
                  class="btn btn-light-primary font-weight-bold mr-2"
                  @click="loadHorarios"
                >
                  Recarregar
                </button>
                <button
                  type="button"
                  class="btn btn-light font-weight-bold"
                  @click="moveDate(1)"
                >
                  Próximo dia
                </button>
              </div>
              <div v-else>
                <div v-if="!hasAvailableSlots" class="agenda-empty mb-5">
                  <h5 class="font-weight-bolder mb-2">
                    Dia sem horários disponíveis.
                  </h5>
                  <p class="text-muted mb-4">
                    Todos os horários deste responsável estão ocupados para a
                    duração selecionada.
                  </p>
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    @click="goToNextAvailableDate"
                  >
                    Buscar próximo disponível
                  </button>
                </div>

                <div
                  v-for="group in slotGroups"
                  :key="group.label"
                  class="agenda-period"
                >
                  <div class="agenda-period-title">
                    <h5>{{ group.label }}</h5>
                    <span
                      >{{ group.available }} livres de {{ group.total }}</span
                    >
                  </div>

                  <div class="agenda-slots">
                    <button
                      v-for="slot in group.slots"
                      :key="slot.inicio"
                      type="button"
                      class="agenda-slot"
                      :class="{
                        selected:
                          selectedSlot && selectedSlot.inicio === slot.inicio,
                        unavailable: !slot.disponivel,
                      }"
                      :disabled="!slot.disponivel"
                      :title="slot.motivo || slotStatusLabel(slot)"
                      @click="selectSlot(slot)"
                    >
                      <ClockOutline :size="16" />
                      <span>{{ slotLabel(slot) }}</span>
                      <small>{{ slotStatusLabel(slot) }}</small>
                    </button>
                  </div>
                </div>
              </div>
            </b-col>

            <b-col md="4">
              <div class="agenda-panel">
                <h5 class="font-weight-bolder mb-4">Resumo do horário</h5>
                <p class="mb-2">
                  <strong>Profissional:</strong>
                  {{ responsavelLabel }}
                </p>
                <p class="mb-2">
                  <strong>Duração:</strong> {{ form.duracao_minutos }} min
                </p>
                <p class="mb-4">
                  <strong>Selecionado:</strong> {{ selectedSlotLabel }}
                </p>
                <div class="agenda-summary-grid mb-5">
                  <div>
                    <strong>{{ totalSlotsCount }}</strong>
                    <span>Total</span>
                  </div>
                  <div>
                    <strong>{{ availableSlotsCount }}</strong>
                    <span>Livres</span>
                  </div>
                  <div>
                    <strong>{{ busySlotsCount }}</strong>
                    <span>Ocupados</span>
                  </div>
                </div>

                <h6 class="font-weight-bolder mb-3">Ocupados no dia</h6>
                <div v-if="horarios.ocupados.length === 0" class="text-muted">
                  Nenhum agendamento ocupando este dia.
                </div>
                <div
                  v-for="ocupado in horarios.ocupados"
                  :key="ocupado.id_agendamento"
                  class="agenda-busy-item"
                >
                  <strong
                    >{{ formatTime(ocupado.data_inicio) }} -
                    {{ formatTime(ocupado.data_fim) }}</strong
                  >
                  <span>{{ ocupado.cliente || "Cliente" }}</span>
                  <small>{{
                    ocupado.servico ||
                    ocupado.descricao_servico ||
                    ocupado.titulo ||
                    "Agendamento"
                  }}</small>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>

        <div v-if="step === 3">
          <b-row>
            <b-col md="7">
              <div class="agenda-review">
                <h5 class="font-weight-bolder mb-5">Conferência</h5>
                <p>
                  <strong>Cliente:</strong>
                  {{ form.cliente ? form.cliente.nome : "-" }}
                </p>
                <p>
                  <strong>Profissional:</strong>
                  {{ responsavelLabel }}
                </p>
                <p><strong>Título:</strong> {{ tituloAgendamentoLabel }}</p>
                <p><strong>Data e horário:</strong> {{ selectedSlotLabel }}</p>
                <p><strong>Duração:</strong> {{ form.duracao_minutos }} min</p>
                <p>
                  <strong>Observações:</strong> {{ form.observacoes || "-" }}
                </p>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>

      <div class="card-footer">
        <div class="d-flex justify-content-center">
          <button
            type="button"
            class="btn btn-light font-weight-bold mr-3"
            @click="step === 1 ? cancel() : step--"
          >
            {{ step === 1 ? "Cancelar" : "Voltar" }}
          </button>
          <button
            v-if="step < 3"
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="step === 2 && !selectedSlot"
            @click="nextStep"
          >
            Próximo
          </button>
          <button
            v-else
            type="button"
            class="btn btn-primary font-weight-bold"
            @click="save"
            ref="submit_agendamento"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>

    <b-modal
      id="novoClienteAgendamentoModal"
      centered
      size="xl"
      title="Novo Cliente"
      hide-footer
      body-class="p-0 novo-cliente-agendamento-modal-body"
      @hidden="resetNovoClienteModal"
    >
      <PessoaForm
        url="/pessoa/create"
        :pessoa="novaPessoa"
        :hide-tipo-pessoa="true"
        :force-cliente="true"
        :redirect-on-save="false"
        @saved="handleNovoClienteSaved"
        @cancel="$bvModal.hide('novoClienteAgendamentoModal')"
      />
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import { mapGetters } from "vuex";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { SistemaMixin } from "@/modules/Application";
import PessoaForm from "@/modules/Pessoa/pages/Form.vue";
import PessoaData from "@/modules/Pessoa/pages/PessoaData";
import ChevronLeft from "vue-material-design-icons/ChevronLeft.vue";
import ChevronRight from "vue-material-design-icons/ChevronRight.vue";
import ClockOutline from "vue-material-design-icons/ClockOutline.vue";

export default {
  name: "app-crm-agendamento-form",
  mixins: [SistemaMixin],
  components: {
    VueSelect,
    PessoaForm,
    ChevronLeft,
    ChevronRight,
    ClockOutline,
  },
  data() {
    return {
      step: 1,
      steps: [
        { id: 1, label: "Dados" },
        { id: 2, label: "Horários" },
        { id: 3, label: "Revisão" },
      ],
      clientes: [],
      usuarios: [],
      novaPessoa: new PessoaData({}),
      loadingHorarios: false,
      searchingNextAvailable: false,
      horariosError: "",
      selectedSlot: null,
      horarios: {
        expediente: null,
        slots: [],
        ocupados: [],
        resumo: null,
      },
      form: {
        cliente: null,
        usuario: null,
        titulo: "",
        data: moment().format("YYYY-MM-DD"),
        duracao_minutos: 30,
        observacoes: "",
        status: "agendado",
      },
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    isUpdate() {
      return !!this.$route.params.id;
    },
    formattedSelectedDate() {
      return moment(this.form.data).format("dddd, DD/MM/YYYY");
    },
    selectedSlotLabel() {
      if (!this.selectedSlot) return "-";
      return `${moment(this.selectedSlot.inicio).format(
        "DD/MM/YYYY HH:mm"
      )} - ${moment(this.selectedSlot.fim).format("HH:mm")}`;
    },
    expedienteLabel() {
      const expediente = this.horarios.expediente;

      if (!expediente) return "08:00 às 18:00";
      if (expediente.fechado) return "Fechado";

      return `${expediente.inicio || "08:00"} às ${expediente.fim || "18:00"}`;
    },
    totalSlotsCount() {
      return this.horarios.slots.length;
    },
    availableSlotsCount() {
      return this.horarios.slots.filter((slot) => slot.disponivel).length;
    },
    busySlotsCount() {
      return this.horarios.slots.filter((slot) => !slot.disponivel).length;
    },
    hasSlots() {
      return this.totalSlotsCount > 0;
    },
    hasAvailableSlots() {
      return this.availableSlotsCount > 0;
    },
    slotGroups() {
      const groups = [
        { label: "Manhã", start: "00:00", end: "11:59", slots: [] },
        { label: "Tarde", start: "12:00", end: "16:59", slots: [] },
        { label: "Fim do dia", start: "17:00", end: "23:59", slots: [] },
      ];

      this.horarios.slots.forEach((slot) => {
        const hora = slot.hora || moment(slot.inicio).format("HH:mm");
        const group = groups.find(
          (item) => hora >= item.start && hora <= item.end
        );

        if (group) {
          group.slots.push(slot);
        }
      });

      return groups
        .filter((group) => group.slots.length)
        .map((group) => ({
          ...group,
          total: group.slots.length,
          available: group.slots.filter((slot) => slot.disponivel).length,
        }));
    },
    tituloAgendamentoLabel() {
      return this.form.titulo || "Agendamento CRM";
    },
    agendaUser() {
      return this.currentUser.admin
        ? this.form.usuario
        : {
            id_usuario: this.currentUser?.id_usuario,
            nome: this.currentUser?.nome || this.currentUser?.usuario,
            usuario: this.currentUser?.usuario,
          };
    },
    responsavelLabel() {
      return this.usuarioLabel(this.agendaUser);
    },
    duracaoValida() {
      const duracao = Number(this.form.duracao_minutos);
      return Number.isInteger(duracao) && duracao >= 1 && duracao <= 1440;
    },
    clienteTelefone() {
      const cliente = this.form.cliente;
      const fone = cliente?.fones?.[0] || cliente?.telefones?.[0];
      return (
        fone?.fone ||
        fone?.telefone ||
        fone?.numero ||
        cliente?.telefone ||
        cliente?.celular ||
        null
      );
    },
    clienteEmail() {
      const cliente = this.form.cliente;
      const email = cliente?.emails?.[0];
      return email?.email || cliente?.email || null;
    },
  },
  methods: {
    goToStep(next) {
      if (next === 1 || this.canLeaveStep(next - 1)) {
        this.step = next;
        if (next === 2) this.loadHorarios();
      }
    },
    nextStep() {
      if (!this.canLeaveStep(this.step)) return;
      this.step += 1;
      if (this.step === 2) this.loadHorarios();
    },
    canLeaveStep(step) {
      if (step === 1) {
        if (!this.form.cliente?.id_pessoa) {
          this.$toast.warning("Selecione o cliente.");
          return false;
        }

        if (!this.agendaUser?.id_usuario) {
          this.$toast.warning("Selecione o responsável da agenda.");
          return false;
        }

        if (!this.duracaoValida) {
          this.$toast.warning(
            "Informe uma duração válida entre 1 e 1440 minutos."
          );
          return false;
        }
      }

      if (step === 2 && !this.selectedSlot) {
        this.$toast.warning("Selecione um horário disponível.");
        return false;
      }

      return true;
    },
    setCliente(cliente) {
      this.form.cliente = cliente;
    },
    async setUsuario(usuario) {
      this.form.usuario = usuario;
      this.clearSelectedSlot();
      this.loadHorarios();
    },
    selectSlot(slot) {
      if (!slot.disponivel) return;
      this.selectedSlot = slot;
    },
    clearSelectedSlot() {
      this.selectedSlot = null;
    },
    changeDate() {
      this.clearSelectedSlot();
      this.loadHorarios();
    },
    changeDuration() {
      this.clearSelectedSlot();
      this.loadHorarios();
    },
    moveDate(days) {
      this.form.data = moment(this.form.data)
        .add(days, "days")
        .format("YYYY-MM-DD");
      this.clearSelectedSlot();
      this.loadHorarios();
    },
    setToday() {
      this.form.data = moment().format("YYYY-MM-DD");
      this.clearSelectedSlot();
      this.loadHorarios();
    },
    normalizeDate(value) {
      const date = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY"], true);

      return date.isValid()
        ? date.format("YYYY-MM-DD")
        : moment(value).format("YYYY-MM-DD");
    },
    applyHorariosResponse(data) {
      this.horarios = {
        expediente: data.expediente || null,
        slots: data.slots || [],
        ocupados: data.ocupados || [],
        resumo: data.resumo || null,
      };
    },
    async fetchHorarios(data) {
      const response = await this.$http.post("/agendamentos/horarios", {
        data,
        id_usuario: this.agendaUser?.id_usuario,
        duracao_minutos: Number(this.form.duracao_minutos),
        id_agendamento: this.$route.params.id || null,
      });

      return response.data;
    },
    async loadHorarios() {
      if (!this.form.data || !this.duracaoValida) {
        this.horariosError = "";
        this.applyHorariosResponse({
          expediente: null,
          slots: [],
          ocupados: [],
        });
        return;
      }

      this.loadingHorarios = true;
      this.horariosError = "";
      try {
        const data = this.normalizeDate(this.form.data);
        this.form.data = data;
        this.applyHorariosResponse(await this.fetchHorarios(data));
      } catch (error) {
        this.applyHorariosResponse({
          expediente: null,
          slots: [],
          ocupados: [],
        });
        this.horariosError =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível carregar os horários disponíveis.";
      } finally {
        this.loadingHorarios = false;
      }
    },
    async goToNextAvailableDate() {
      if (!this.duracaoValida) {
        this.$toast.warning("Informe uma duração válida.");
        return;
      }

      this.searchingNextAvailable = true;
      this.loadingHorarios = true;
      this.horariosError = "";

      try {
        const start = moment(this.form.data);

        for (let day = 1; day <= 30; day += 1) {
          const data = start.clone().add(day, "days").format("YYYY-MM-DD");
          const horarios = await this.fetchHorarios(data);
          const hasAvailable = (horarios.slots || []).some(
            (slot) => slot.disponivel
          );

          if (hasAvailable) {
            this.form.data = data;
            this.clearSelectedSlot();
            this.applyHorariosResponse(horarios);
            return;
          }
        }

        this.horariosError =
          "Não encontrei horários livres nos próximos 30 dias.";
      } catch (error) {
        this.horariosError =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível buscar o próximo horário disponível.";
      } finally {
        this.loadingHorarios = false;
        this.searchingNextAvailable = false;
      }
    },
    slotLabel(slot) {
      const inicio = slot.hora || moment(slot.inicio).format("HH:mm");
      const fim = slot.fim ? moment(slot.fim).format("HH:mm") : "";

      return fim ? `${inicio} - ${fim}` : inicio;
    },
    slotStatusLabel(slot) {
      return slot.disponivel ? "Livre" : slot.motivo || "Ocupado";
    },
    async save() {
      if (!this.canLeaveStep(2)) return;

      this.addLoadingToButton("submit_agendamento");
      try {
        const payload = {
          id_pessoa: this.form.cliente.id_pessoa,
          id_usuario: this.agendaUser?.id_usuario,
          titulo: this.tituloAgendamentoLabel,
          data_inicio: this.selectedSlot.inicio,
          duracao_minutos: Number(this.form.duracao_minutos),
          status: this.form.status || "agendado",
          observacoes: this.form.observacoes,
        };

        if (this.isUpdate) {
          payload.id_agendamento = this.$route.params.id;
        }

        const endpoint = this.isUpdate
          ? this.$route.query.reagendar
            ? "/crm/agendamentos/reagendar"
            : "/crm/agendamentos/update"
          : "/crm/agendamentos/create";

        if (this.$route.query.reagendar) {
          payload.motivo_reagendamento = this.$route.query.motivo;
        }

        await this.$http.post(endpoint, payload);

        this.$toast.success("Agendamento salvo com sucesso!");
        this.$router.push({ name: "crmAgenda" });
      } finally {
        this.removeLoadingToButton("submit_agendamento");
      }
    },
    cancel() {
      this.$router.push({ name: "crmAgenda" });
    },
    formatTime(value) {
      return value ? moment(value).format("HH:mm") : "--:--";
    },
    formatDuration(value) {
      const minutes = Number(value);
      if (!minutes) return "-";
      if (minutes < 60) return `${minutes} min`;

      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;
      return rest ? `${hours}h${String(rest).padStart(2, "0")}` : `${hours}h`;
    },
    usuarioLabel(usuario) {
      return usuario?.nome || usuario?.usuario || "-";
    },
    openNovoClienteModal() {
      this.novaPessoa = new PessoaData({});
      this.$bvModal.show("novoClienteAgendamentoModal");
    },
    resetNovoClienteModal() {
      this.novaPessoa = new PessoaData({});
    },
    async handleNovoClienteSaved({ response, form }) {
      try {
        const cliente = await this.resolveClienteCriado(response, form);

        if (!cliente?.id_pessoa) {
          this.$toast.warning(
            "Cliente cadastrado, mas não foi possível selecioná-lo automaticamente."
          );
          this.$bvModal.hide("novoClienteAgendamentoModal");
          return;
        }

        this.clientes = [cliente];
        this.setCliente(cliente);
        this.$bvModal.hide("novoClienteAgendamentoModal");
        this.$toast.success("Cliente cadastrado e selecionado!");
      } catch (error) {
        this.$toast.error(
          "Cliente cadastrado, mas houve erro ao selecionar no agendamento."
        );
      }
    },
    async resolveClienteCriado(response, form) {
      const responseData = response?.data ?? response;
      const id_pessoa = responseData?.id_pessoa ?? responseData?.id;
      const cliente =
        responseData?.pessoa ??
        responseData?.cliente ??
        responseData?.data ??
        responseData;

      if (cliente?.id_pessoa) return cliente;

      if (id_pessoa) {
        const { data } = await this.$http.post("/pessoa/find", { id_pessoa });
        return data?.pessoa ?? data?.data ?? data;
      }

      const clientes = await this.getClientes(form?.nome ?? "");
      return clientes.find((item) => item.nome === form?.nome) ?? clientes[0];
    },
    onSearchCliente(search, loading) {
      if (search.length) {
        loading(true);
        this.searchCliente(loading, search, this);
      }
    },
    searchCliente: _.debounce(async (loading, search, vm) => {
      vm.clientes = await vm.getClientes(search);
      loading(false);
    }, 350),
    onSearchUsuario(search, loading) {
      if (search.length) {
        loading(true);
        this.searchUsuario(loading, search, this);
      }
    },
    searchUsuario: _.debounce(async (loading, search, vm) => {
      await vm.loadUsuarios(search);
      loading(false);
    }, 350),
    async loadUsuarios(search = "") {
      if (!this.currentUser.admin) {
        this.form.usuario = this.agendaUser;
        return;
      }

      const { data } = await this.$http.post("/usuario/get-usuarios", {
        search,
      });
      this.usuarios = Array.isArray(data) ? data : data.data || [];

      const queryUserId = Number(this.$route.query.id_usuario);
      const currentUserId = this.currentUser?.id_usuario;
      const selectedId =
        this.form.usuario?.id_usuario || queryUserId || currentUserId;
      const selected = this.usuarios.find((usuario) => {
        return Number(usuario.id_usuario) === Number(selectedId);
      });

      if (!this.form.usuario) {
        this.form.usuario = selected || this.usuarios[0] || null;
      }
    },
    async loadAgendamento() {
      const { data } = await this.$http.post("/agendamentos/find", {
        id_agendamento: this.$route.params.id,
      });

      this.form = {
        cliente: data.pessoa,
        usuario: data.usuario,
        titulo: data.titulo || "",
        data: moment(data.data_inicio).format("YYYY-MM-DD"),
        duracao_minutos: data.duracao_minutos,
        observacoes: data.observacoes || "",
        status: data.status || "agendado",
      };

      this.clientes = data.pessoa ? [data.pessoa] : [];
      this.usuarios = data.usuario
        ? [data.usuario, ...this.usuarios]
        : this.usuarios;
      this.selectedSlot = {
        inicio: moment(data.data_inicio).format("YYYY-MM-DD HH:mm:ss"),
        fim: moment(data.data_fim).format("YYYY-MM-DD HH:mm:ss"),
        hora: moment(data.data_inicio).format("HH:mm"),
        disponivel: true,
      };
      await this.loadHorarios();
    },
  },
  async created() {
    moment.locale("pt-br");
    await this.loadUsuarios();
    if (this.isUpdate) {
      await this.loadAgendamento();
    }
  },
};
</script>

<style scoped>
.agenda-steps {
  display: flex;
  gap: 10px;
  border-bottom: 1px solid #ebedf3;
  padding-bottom: 18px;
}

.agenda-step {
  border: 0;
  background: #f3f6f9;
  color: #7e8299;
  font-weight: 700;
  padding: 10px 16px;
  border-radius: 6px;
}

.agenda-step span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 50%;
  background: #e4e6ef;
  color: #3f4254;
}

.agenda-step.active,
.agenda-step.done {
  background: #e1f0ff;
  color: #3699ff;
}

.cliente-resumo,
.agenda-panel,
.agenda-review {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 18px;
  background: #fff;
}

.agenda-calendar-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.agenda-date-input {
  max-width: 220px;
}

.agenda-duration-readonly {
  background: #f3f6f9;
  color: #7e8299;
  font-weight: 700;
}

.agenda-day-title {
  margin-bottom: 18px;
}

.agenda-day-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.agenda-day-meta span {
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  padding: 6px 10px;
  color: #7e8299;
  font-weight: 600;
  background: #fff;
}

.agenda-loading {
  padding: 24px;
  color: #7e8299;
}

.agenda-empty {
  border: 1px dashed #d1d3e0;
  border-radius: 6px;
  padding: 22px;
  background: #fafafa;
}

.agenda-period {
  margin-bottom: 22px;
}

.agenda-period-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.agenda-period-title h5 {
  margin: 0;
  font-weight: 700;
  color: #3f4254;
}

.agenda-period-title span {
  color: #7e8299;
  font-weight: 600;
}

.agenda-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.agenda-slot {
  min-height: 78px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #fff;
  color: #3f4254;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  gap: 4px;
}

.agenda-slot small {
  color: #7e8299;
  font-weight: 600;
}

.agenda-slot.selected {
  border-color: #3699ff;
  background: #e1f0ff;
  color: #3699ff;
}

.agenda-slot.unavailable {
  background: #f3f6f9;
  color: #b5b5c3;
  cursor: not-allowed;
}

.agenda-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.agenda-summary-grid div {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 10px;
  text-align: center;
  background: #fafafa;
}

.agenda-summary-grid strong,
.agenda-summary-grid span {
  display: block;
}

.agenda-summary-grid strong {
  color: #3f4254;
}

.agenda-summary-grid span {
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
}

.agenda-busy-item {
  border-left: 3px solid #ffa800;
  padding: 8px 0 8px 12px;
  margin-bottom: 10px;
  background: #fff8dd;
}

.agenda-busy-item span,
.agenda-busy-item small {
  display: block;
}
</style>
